/**
 * Edit User Modal — extracted from AdminDashboard.jsx.
 *
 * Purely presentational. Receives the open `user` (which doubles as the
 * open/closed flag — null = closed) and writes mutations back via
 * `setUser(updated)`. The save action is delegated to `onSave`. The
 * password-reset shortcut buttons fire `onSendReset(user)` and
 * `onSetPasswordDirect(user)` so the parent can chain them with the
 * password-reset and direct-password modals.
 */
import React from 'react';
import { Mail, KeyRound } from 'lucide-react';
import {
  Dialog, DialogContent, DialogDescription, DialogFooter, DialogHeader, DialogTitle,
} from '@/components/ui/dialog';
import { Button } from '@/components/ui/button';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Checkbox } from '@/components/ui/checkbox';
import {
  Select, SelectContent, SelectItem, SelectTrigger, SelectValue,
} from '@/components/ui/select';

export function EditUserModal({
  user,
  setUser,
  onSave,
  onSendReset,
  onSetPasswordDirect,
}) {
  const close = () => setUser(null);

  return (
    <Dialog open={!!user} onOpenChange={(open) => !open && close()}>
      <DialogContent className="flex flex-col h-[90vh] sm:h-auto sm:max-h-[90vh] p-0 gap-0 overflow-hidden">
        <DialogHeader className="px-6 pt-6 pb-4 border-b shrink-0">
          <DialogTitle>Edit User</DialogTitle>
          <DialogDescription>
            Update user details and subscription status
          </DialogDescription>
        </DialogHeader>

        {user && (
          <div className="flex-1 min-h-0 overflow-y-auto px-6 py-4 space-y-4">
            <div className="space-y-2">
              <Label>Email</Label>
              <Input value={user.email} disabled className="bg-muted" />
            </div>
            <div className="space-y-2">
              <Label htmlFor="edit-name">Name</Label>
              <Input
                id="edit-name"
                value={user.name}
                onChange={(e) => setUser({ ...user, name: e.target.value })}
                placeholder="First name only — no last names please (nicknames welcome)"
              />
              <p className="text-xs text-muted-foreground">First name only — no last names please. Nicknames welcome.</p>
            </div>
            <div className="space-y-2">
              <Label>Subscription Status</Label>
              <Select
                value={user.subscription_status}
                onValueChange={(value) => setUser({ ...user, subscription_status: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="free">Free</SelectItem>
                  <SelectItem value="trial">Trial</SelectItem>
                  <SelectItem value="active">Active</SelectItem>
                  <SelectItem value="expired">Expired</SelectItem>
                  <SelectItem value="cancelled">Cancelled</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label>Subscription Tier</Label>
              <Select
                value={user.subscription_tier || 'individual'}
                onValueChange={(value) => setUser({ ...user, subscription_tier: value })}
              >
                <SelectTrigger><SelectValue /></SelectTrigger>
                <SelectContent>
                  <SelectItem value="individual">Individual ($1.99/mo)</SelectItem>
                  <SelectItem value="family">Family & Friends ($2.99/mo)</SelectItem>
                  <SelectItem value="cookout">The Whole Cookout ($5.99/mo)</SelectItem>
                  <SelectItem value="reunion">Family Reunion ($12.99/mo)</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-is_gifted"
                checked={user.is_gifted || false}
                onCheckedChange={(checked) => setUser({ ...user, is_gifted: checked })}
              />
              <Label htmlFor="edit-is_gifted">Gifted subscription (free, no Stripe)</Label>
            </div>
            <p className="text-xs text-muted-foreground">
              To give free access: Set status to &quot;Active&quot;, check &quot;Gifted subscription&quot;, and save.
            </p>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-is_admin"
                checked={user.is_admin}
                onCheckedChange={(checked) => setUser({ ...user, is_admin: checked })}
              />
              <Label htmlFor="edit-is_admin">Admin privileges</Label>
            </div>
            <div className="flex items-center space-x-2">
              <Checkbox
                id="edit-is_contributor"
                checked={user.is_contributor || false}
                onCheckedChange={(checked) => setUser({ ...user, is_contributor: checked })}
              />
              <Label htmlFor="edit-is_contributor">Blog contributor (can create/edit blog posts)</Label>
            </div>

            {/* Inline password actions — same handlers the row icons fire,
                surfaced here so admins on mobile don't need to find them. */}
            <div className="text-sm bg-muted/50 p-3 rounded border space-y-2">
              <p className="font-medium">Password</p>
              <div className="flex flex-wrap gap-2">
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onSendReset(user)}
                  data-testid="modal-send-reset-email"
                >
                  <Mail className="h-4 w-4 mr-2 text-blue-500" />
                  Send reset email
                </Button>
                <Button
                  type="button"
                  variant="outline"
                  size="sm"
                  onClick={() => onSetPasswordDirect(user)}
                  data-testid="modal-set-password-directly"
                >
                  <KeyRound className="h-4 w-4 mr-2 text-green-500" />
                  Set password directly
                </Button>
              </div>
            </div>

            <div className="text-sm bg-muted/50 p-3 rounded border">
              <p className="font-medium mb-2">Payment Information</p>
              <div className="space-y-1 text-muted-foreground">
                <p>
                  <span className="font-medium">Method:</span>{' '}
                  {user.is_gifted ? '🎁 Gifted (Free)' :
                    user.stripe_subscription_id ? '💳 Stripe' : '—'}
                </p>
                {user.stripe_customer_id && (
                  <p><span className="font-medium">Stripe Customer:</span> {user.stripe_customer_id}</p>
                )}
                {user.stripe_subscription_id && (
                  <p><span className="font-medium">Stripe Subscription:</span> {user.stripe_subscription_id}</p>
                )}
              </div>
            </div>
          </div>
        )}

        <DialogFooter className="px-6 py-4 border-t bg-background shrink-0">
          <Button variant="outline" onClick={close} data-testid="edit-user-cancel">
            Cancel
          </Button>
          <Button onClick={onSave} data-testid="edit-user-save">Save Changes</Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}

export default EditUserModal;
